import { useState, useCallback, useEffect, useRef } from 'react';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { publishReport, unpublishReport } from '@/lib/api/endpoints/workspaces';
import { env } from '@/lib/env';
import { THEMES } from './themes';
import type { ReportTheme } from '@/types/report';

interface Props {
  message?: string;
  isComplete: boolean;
  isError?: boolean;
  moduleId?: string;
  publishUrl?: string;
  currentTheme?: ReportTheme;
  onThemeChange?: (theme: ReportTheme) => void;
  themePickerForceOpen?: boolean;
  onThemePickerClosed?: () => void;
  onPublished?: (slug: string, url: string) => void;
  onUnpublished?: () => void;
  onDownload?: () => void;
}

/** Resolve relative URLs (local dev /pub/slug) against the API base. */
function resolveUrl(url: string): string {
  if (url.startsWith('http')) return url;
  return `${env.apiBaseUrl}${url}`;
}

export function ReportStatusBar({
  message,
  isComplete,
  isError,
  moduleId,
  publishUrl: initialPublishUrl,
  currentTheme,
  onThemeChange,
  themePickerForceOpen,
  onThemePickerClosed,
  onPublished,
  onUnpublished,
  onDownload,
}: Props) {
  const { token, selectedContainer } = useSession();

  // Minimum display time for status messages (2.5s) so they don't flash by
  const [displayMessage, setDisplayMessage] = useState(message);
  const messageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMessageTimeRef = useRef(0);

  useEffect(() => {
    if (!message || isComplete || isError) {
      // Terminal states update immediately
      if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
      setDisplayMessage(message);
      return;
    }
    const now = Date.now();
    const elapsed = now - lastMessageTimeRef.current;
    const minDelay = 2500; // 2.5 seconds minimum per message

    if (elapsed >= minDelay) {
      // Enough time passed, update immediately
      setDisplayMessage(message);
      lastMessageTimeRef.current = now;
    } else {
      // Queue the update for after the remaining delay
      if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
      messageTimerRef.current = setTimeout(() => {
        setDisplayMessage(message);
        lastMessageTimeRef.current = Date.now();
      }, minDelay - elapsed);
    }

    return () => { if (messageTimerRef.current) clearTimeout(messageTimerRef.current); };
  }, [message, isComplete, isError]);

  const [publishing, setPublishing] = useState(false);
  const [publicUrl, setPublicUrl] = useState(initialPublishUrl);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);

  // Sync from props when switching between reports
  useEffect(() => { setPublicUrl(initialPublishUrl); }, [initialPublishUrl]);

  const themePickerRef = useRef<HTMLDivElement>(null);

  // Sync force-open from parent (e.g. ModuleMenu "Change Theme" click)
  useEffect(() => {
    if (themePickerForceOpen) setThemeDropdownOpen(true);
  }, [themePickerForceOpen]);

  // Use selectedContainer for workspace ID, fall back to getActiveWorkspaceId
  const resolveWorkspaceId = useCallback(() => {
    return selectedContainer?.id ?? selectedContainer?.ownerId ?? getActiveWorkspaceId();
  }, [selectedContainer]);

  const handlePublish = useCallback(async () => {
    if (!moduleId || publishing) return;
    setPublishing(true);
    setError(null);
    try {
      const workspaceId = resolveWorkspaceId();
      const result = await publishReport(token ?? null, workspaceId, moduleId);
      setPublicUrl(result.url);
      onPublished?.(result.slug, result.url);
    } catch (err) {
      console.error('Failed to publish report:', err);
      setError('Failed to publish');
      setTimeout(() => setError(null), 3000);
    } finally {
      setPublishing(false);
    }
  }, [moduleId, token, publishing, onPublished, resolveWorkspaceId]);

  const handleUnpublish = useCallback(async () => {
    if (!moduleId) return;
    setError(null);
    try {
      const workspaceId = resolveWorkspaceId();
      await unpublishReport(token ?? null, workspaceId, moduleId);
      setPublicUrl(undefined);
      onUnpublished?.();
    } catch (err) {
      console.error('Failed to unpublish report:', err);
      setError('Failed to unpublish');
      setTimeout(() => setError(null), 3000);
    }
  }, [moduleId, token, onUnpublished, resolveWorkspaceId]);

  const resolvedUrl = publicUrl ? resolveUrl(publicUrl) : null;

  // Two distinct actions, two distinct buttons — same split as PageRenderer.
  // Copy unconditionally writes to clipboard; the Share button below uses
  // handleNativeShare. The previous combined handler routed to
  // navigator.share whenever it existed, so on Chrome/Safari (including
  // desktop) both the copy-link icon and the Share button opened the OS
  // share sheet instead of copying.
  const handleCopy = useCallback(async () => {
    if (!resolvedUrl) return;
    try {
      await navigator.clipboard.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('[ReportStatusBar] clipboard.writeText failed', err);
    }
  }, [resolvedUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!resolvedUrl) return;
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return;
    try {
      await navigator.share({ url: resolvedUrl });
    } catch {
      // User cancelled share — not an error
    }
  }, [resolvedUrl]);

  const closeThemeDropdown = useCallback(() => {
    setThemeDropdownOpen(false);
    onThemePickerClosed?.();
  }, [onThemePickerClosed]);

  // Close theme dropdown on outside click
  useEffect(() => {
    if (!themeDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (themePickerRef.current && !themePickerRef.current.contains(e.target as Node)) {
        closeThemeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [themeDropdownOpen, closeThemeDropdown]);

  const handleRetry = useCallback(() => {
    window.dispatchEvent(new CustomEvent('xavior:send-chat-message', {
      detail: { message: 'Continue building this report' },
    }));
  }, []);

  return (
    <div className={`report-status-bar ${isComplete ? 'complete' : ''}${isError ? ' error' : ''}`}>
      <span className="status-dot" />
      <span>{error || (isError ? (displayMessage || 'Report generation failed') : isComplete ? 'Done' : displayMessage || 'Building...')}</span>
      {isError && (
        <button className="report-retry-btn" onClick={handleRetry} title="Retry report generation">
          Retry
        </button>
      )}

      {isComplete && moduleId && (
        <>
          <div ref={themePickerRef} className="report-theme-picker" style={{ position: 'relative' }}>
            <button
              className="report-theme-btn"
              onClick={() => themeDropdownOpen ? closeThemeDropdown() : setThemeDropdownOpen(true)}
              title="Change theme"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                <path d="M2 12h20" />
              </svg>
              {currentTheme ?? 'formal'}
            </button>
            {themeDropdownOpen && (
              <div className="report-theme-dropdown xv-scroll">
                {(Object.keys(THEMES) as ReportTheme[]).map(name => (
                  <button
                    key={name}
                    className={`report-theme-option ${name === currentTheme ? 'active' : ''}`}
                    onClick={() => {
                      onThemeChange?.(name);
                      closeThemeDropdown();
                    }}
                  >
                    <span
                      className="report-theme-swatch"
                      style={{
                        background: THEMES[name].variables['--report-bg'],
                        borderColor: THEMES[name].variables['--report-accent'],
                      }}
                    />
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="report-share-controls">
            {/* Order mirrors PageRenderer's webpage action bar:
                  Pre-publish:  Theme → Download → Publish
                  Post-publish: Theme → Download → Copy → Share → Unpublish
                Primary action (Publish or Unpublish) is always last.
                Colors stay on the existing theme tokens — only the icon
                vocabulary, ordering, and spinner are changing. */}

            {/* Download — always available (icon button). */}
            {onDownload && (
              <button
                type="button"
                className="report-share-icon-btn"
                onClick={onDownload}
                aria-label="Download HTML"
                title="Download"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5" />
                  <path d="M7.5 12L12 16.5m0 0L16.5 12M12 16.5V3" />
                </svg>
              </button>
            )}

            {/* Copy link — only after publish (no URL to copy beforehand).
                Icon swaps to a checkmark for ~2s after a successful copy. */}
            {publicUrl && (
              <button
                type="button"
                className="report-share-icon-btn"
                onClick={handleCopy}
                title={copied ? 'Copied!' : 'Copy link'}
                aria-label={copied ? 'Copied!' : 'Copy link'}
              >
                {copied ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
            )}

            {/* Share — post-publish only, opens the native share sheet
                when the browser supports navigator.share. */}
            {publicUrl && typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
              <button className="report-share-link-btn" onClick={handleNativeShare} title="Open share sheet">
                Share
              </button>
            )}

            {/* Primary action — always rightmost.
                  Pre-publish:  Publish (with inline spinner that swaps in
                                while a publish is in flight; button width
                                is reserved so the toolbar doesn't jump).
                  Post-publish: Unpublish (inverted-Publish styling). */}
            {publicUrl ? (
              <button className="report-unpublish-btn" onClick={handleUnpublish} title="Unpublish">
                Unpublish
              </button>
            ) : (
              <button
                className="report-publish-btn"
                onClick={handlePublish}
                disabled={publishing}
                aria-label={publishing ? 'Publishing…' : 'Publish'}
              >
                {publishing ? (
                  <svg
                    className="report-publish-spinner"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  'Publish'
                )}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
